import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { countOf } from '@/utils/plural';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  UserIcon,
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { apiGet } from '@/utils/api';
import { useNarrow } from '@/hooks/useNarrow';
import { displayDateTime } from '@/utils/dateDisplay';

/** "in_progress" -> "In progress": a status as words, never a raw value. */
function humanise(value: string): string {
  const t = value.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

interface SearchResult {
  id: string;
  type: 'ticket' | 'asset' | 'visitor' | 'work_order' | 'lease' | 'space'
    // Employee rebuild B26/C13: what a person searches for.
    | 'person' | 'room' | 'my_booking' | 'my_request' | 'help';
  title: string;
  subtitle: string;
  url: string;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

type TypeConfig = { icon: React.ElementType; color: string; bg: string };

const FALLBACK_CONFIG: TypeConfig = {
  icon: TicketIcon,
  color: 'text-orn-700',
  bg: 'bg-orn-50',
};

const TYPE_CONFIG: Record<string, TypeConfig> = {
  ticket: FALLBACK_CONFIG,
  asset: { icon: WrenchScrewdriverIcon, color: 'text-blue-600', bg: 'bg-blue-50' },
  visitor: { icon: UserGroupIcon, color: 'text-teal-600', bg: 'bg-teal-50' },
  work_order: { icon: WrenchScrewdriverIcon, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  lease: { icon: DocumentTextIcon, color: 'text-purple-600', bg: 'bg-purple-50' },
  space: { icon: BuildingOfficeIcon, color: 'text-green-700', bg: 'bg-green-50' },
  person: { icon: UserIcon, color: 'text-brand-700', bg: 'bg-brand-50' },
  room: { icon: BuildingOfficeIcon, color: 'text-green-700', bg: 'bg-green-50' },
  my_booking: { icon: CalendarDaysIcon, color: 'text-blue-600', bg: 'bg-blue-50' },
  my_request: { icon: ChatBubbleLeftEllipsisIcon, color: 'text-orn-700', bg: 'bg-orn-50' },
  help: { icon: BookOpenIcon, color: 'text-purple-600', bg: 'bg-purple-50' },
};

/** Each group's heading, in the order the palette shows them: the things a
 *  person looks for first (C13), then the staff registers. */
const GROUP_LABEL: Record<SearchResult['type'], string> = {
  person: 'People',
  room: 'Rooms',
  my_booking: 'My bookings',
  my_request: 'My requests',
  help: 'Help',
  ticket: 'Tickets',
  asset: 'Assets',
  work_order: 'Work orders',
  visitor: 'Visitors',
  lease: 'Leases',
  space: 'Spaces',
};
const GROUP_ORDER = Object.keys(GROUP_LABEL) as SearchResult['type'][];

/** What the row's action says, where it is not just "open". */
const ACTION_LABEL: Partial<Record<SearchResult['type'], string>> = {
  person: 'Find',
  room: 'Book',
  help: 'Read',
};

const CATEGORY_TO_TYPE: Record<string, SearchResult['type']> = {
  tickets: 'ticket',
  assets: 'asset',
  work_orders: 'work_order',
  visitors: 'visitor',
  leases: 'lease',
  people: 'person',
  rooms: 'room',
  my_bookings: 'my_booking',
  my_requests: 'my_request',
  help: 'help',
};

// Frontend owns its own route map — synthesise URLs from type+id rather
// than trusting whatever the backend templated.
const URL_FOR_TYPE: Record<SearchResult['type'], (id: string) => string> = {
  ticket: (id) => `/helpdesk/${id}`,
  asset: (id) => `/assets/${id}`,
  work_order: (id) => `/maintenance/work-orders/${id}`,
  visitor: (id) => `/visitors/${id}`,
  lease: (id) => `/leases/${id}`,
  space: (id) => `/spaces/${id}`,
  room: (id) => `/book?tab=room&room=${id}`,
  my_request: (id) => `/my-requests/${id}`,
  help: (id) => `/my-requests?tab=help&article=${id}`,
  // A person and a booking open a page that is not keyed by their id (Who's
  // in by name; My bookings, where a desk booking is highlighted). The server
  // builds those paths; resultUrl below accepts only an in-app path.
  person: () => '/whos-in?tab=find',
  my_booking: () => '/my-bookings',
};

/** The in-app path for a result: ours for an id-keyed page, the server's for
 *  the two that are not — and only ever a path on this app. */
function resultUrl(type: SearchResult['type'], id: string, serverUrl?: string): string {
  if ((type === 'person' || type === 'my_booking')
      && typeof serverUrl === 'string' && serverUrl.startsWith('/') && !serverUrl.startsWith('//')) {
    return serverUrl;
  }
  return URL_FOR_TYPE[type](id);
}

// The categories' names as a person reads them, for the "could not be
// searched" line below.
const CATEGORY_LABEL: Record<string, string> = {
  tickets: 'tickets',
  assets: 'assets',
  work_orders: 'work orders',
  visitors: 'visitors',
  leases: 'leases',
  people: 'people',
  rooms: 'rooms',
  my_bookings: 'your bookings',
  my_requests: 'your requests',
  help: 'help articles',
};

interface BackendItem {
  id: string;
  title: string;
  url?: string;
  status?: string;
  priority?: string;
  category?: string;
  condition?: string;
  type?: string;
  company?: string;
  total_visits?: number;
  landlord?: string;
  department?: string;
  in_office_today?: boolean | null;
  building?: string;
  floor?: string;
  capacity?: number | null;
  kind?: string;
  when?: string;
  number?: string | null;
}

function subtitleFor(type: SearchResult['type'], item: BackendItem): string {
  switch (type) {
    case 'ticket':
      return [item.status, item.priority, item.category].filter(Boolean).join(' · ');
    case 'asset':
      return [item.status, item.condition].filter(Boolean).join(' · ');
    case 'work_order':
      return [item.status, item.priority, item.type].filter(Boolean).join(' · ');
    case 'visitor':
      return item.company || (item.total_visits != null ? `${item.total_visits} visits` : '');
    case 'lease':
      return item.landlord || '';
    case 'person':
      // Never a phone or an email — the server sends neither. "In office" only
      // when Who's in would say so; null means it is not ours to say.
      return [item.department, item.in_office_today === true ? 'In office'
        : item.in_office_today === false ? 'Not in office' : null]
        .filter(Boolean).join(' · ');
    case 'room':
      return [item.floor, item.building, item.capacity ? countOf(item.capacity, 'seat') : null]
        .filter(Boolean).join(' · ');
    case 'my_booking':
      return item.when ? displayDateTime(item.when) : '';
    case 'my_request':
      return [item.number, item.status ? humanise(item.status) : null].filter(Boolean).join(' · ');
    default:
      return '';
  }
}

function flattenResults(categorised: Record<string, BackendItem[]> | undefined): SearchResult[] {
  if (!categorised) return [];
  const flat: SearchResult[] = [];
  for (const [category, items] of Object.entries(categorised)) {
    const type = CATEGORY_TO_TYPE[category];
    if (!type || !Array.isArray(items)) continue;
    for (const item of items) {
      flat.push({
        id: item.id,
        type,
        title: item.title,
        subtitle: subtitleFor(type, item),
        url: resultUrl(type, item.id, item.url),
      });
    }
  }
  return flat;
}

export default function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const narrowScreen = useNarrow();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  // Categories the server could not search this time (EXT-12). Shown, so a
  // failed category is not mistaken for one with nothing in it.
  const [unavailable, setUnavailable] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Focus input when opened, reset when closed
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setResults([]);
      setUnavailable([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Defensive document-level Escape handler — Headless UI Dialog should
  // catch Escape via its focus trap, but if another component captures
  // the event in the capture phase first, the Dialog never sees it.
  // Listening on document in the capture phase guarantees we hear it.
  // (Deep-test EXT-13: the five console.log lines that traced this handler
  // were a debugging session's, and wrote to every user's console.)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [isOpen, onClose]);

  // Debounced search
  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setUnavailable([]);
      return;
    }
    setIsLoading(true);
    try {
      const data = await apiGet<{
        results: Record<string, BackendItem[]>;
        unavailable?: string[];
      }>(`/search/global?q=${encodeURIComponent(q)}`);
      setResults(flattenResults(data?.results));
      setUnavailable(Array.isArray(data?.unavailable) ? data.unavailable : []);
    } catch {
      setResults([]);
      setUnavailable([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setSelectedIndex(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleSelect = (result: SearchResult) => {
    const url = result.url || URL_FOR_TYPE[result.type]?.(result.id);
    if (!url) {
      console.error('[GlobalSearch] no URL for result', result);
      toast.error(`Cannot open ${result.type} — unknown type`);
      return;
    }
    onClose();
    // Defer navigation past the current tick so the Dialog's onClose state
    // update commits before react-router unmounts/remounts the route tree.
    setTimeout(() => navigate(url), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex]);
    }
  };

  // Group results by type
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    const bucket = acc[r.type] ?? (acc[r.type] = []);
    bucket.push(r);
    return acc;
  }, {});
  const groups = GROUP_ORDER.filter((t) => grouped[t]?.length)
    .map((t) => [t, grouped[t]!] as const);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-scrim/40 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />
        </Transition.Child>

        <div className="fixed inset-0 flex items-start justify-center p-0 sm:pt-[15vh] sm:p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 sm:scale-95"
            enterTo="opacity-100 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 sm:scale-100"
            leaveTo="opacity-0 sm:scale-95"
          >
            {/* On a phone the sheet fades in at full width; it zooms only from
                sm up, where it is a centred window (a scaled sheet is narrower
                than the screen while it opens). */}
            <Dialog.Panel className="w-full max-w-none sm:max-w-xl rounded-b-2xl sm:rounded-2xl bg-surface shadow-2xl overflow-hidden">
              {/* Search input */}
              <div className="flex items-center gap-3 border-b border-line px-4 py-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-content-subtle shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={narrowScreen ? 'Search people, rooms, help…' : 'Search people, rooms, bookings, requests, help…'}
                  aria-label="Search"
                  className="flex-1 bg-transparent text-sm text-content placeholder:text-content-subtle outline-none"
                />
                <kbd className="hidden sm:inline-flex rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-mono text-content-muted">
                  ESC
                </kbd>
              </div>

              {/* Results */}
              {/* vh, not px. max-h-80 is 320px whatever the window is, so on a
                  short viewport the list alone outgrew the space left under the
                  15vh offset and the input row, and the palette ran off the
                  bottom with no way to reach the last result. A vh cap cannot:
                  15vh offset + 60vh list + the input row still fits. */}
              <div className="max-h-[60vh] overflow-y-auto p-2">
                {isLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                  </div>
                )}

                {!isLoading && unavailable.length > 0 && (
                  <p
                    role="status"
                    data-testid="search-unavailable"
                    className="px-3 py-2 text-xs text-amber-700"
                  >
                    {`Could not search ${unavailable
                      .map((c) => CATEGORY_LABEL[c] ?? c.replace(/_/g, ' '))
                      .join(', ')} just now. Try again in a moment.`}
                  </p>
                )}

                {!isLoading && query.length >= 2 && results.length === 0 && unavailable.length === 0 && (
                  <p className="py-8 text-center text-sm text-content-subtle">No results found</p>
                )}

                {!isLoading && query.length < 2 && (
                  <div className="py-6 text-center text-sm text-content-subtle">
                    <p>Type at least 2 characters to search</p>
                    <p className="mt-1 text-xs">
                      <kbd className="rounded bg-surface-muted px-1 py-0.5 font-mono">Cmd+K</kbd> to open anytime
                    </p>
                  </div>
                )}

                {groups.map(([type, items]) => {
                  const config = TYPE_CONFIG[type] ?? FALLBACK_CONFIG;
                  return (
                    <div key={type} className="mb-2" data-testid={`search-group-${type}`}>
                      <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-content-subtle">
                        {GROUP_LABEL[type]}
                      </p>
                      {items.map((item) => {
                        const Icon = config.icon;
                        const flatIndex = results.indexOf(item);
                        return (
                          <button
                            key={item.id}
                            type="button"
                            data-testid={`search-result-${item.type}-${item.id}`}
                            onClick={() => handleSelect(item)}
                            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                              flatIndex === selectedIndex ? 'bg-brand-50 text-brand-700' : 'hover:bg-surface-subtle'
                            }`}
                          >
                            <div className={`pointer-events-none flex h-8 w-8 items-center justify-center rounded-lg ${config.bg}`}>
                              <Icon className={`h-4 w-4 ${config.color}`} />
                            </div>
                            <div className="pointer-events-none min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-content">{item.title}</p>
                              <p className="text-xs text-content-subtle">{item.subtitle}</p>
                            </div>
                            {ACTION_LABEL[item.type] && (
                              <span className="pointer-events-none shrink-0 text-xs font-medium text-brand-700">
                                {ACTION_LABEL[item.type]}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center gap-4 border-t border-line-subtle px-4 py-2 text-[10px] text-content-subtle">
                <span><kbd className="font-mono">↑↓</kbd> navigate</span>
                <span><kbd className="font-mono">Enter</kbd> select</span>
                <span><kbd className="font-mono">Esc</kbd> close</span>
                <span className="ml-auto hidden sm:inline">
                  Search covers people, rooms, my bookings, my requests and help.
                </span>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
